import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { api } from '@culture/common-helpers/request';
import { getImageUrl } from '@culture/common-helpers/cultureUtils';
import { instituteTypeTitlesHash } from '@culture/utils/helpers/institutes';
import { objectTypeTitlesHash } from '@culture/utils/helpers/objects';
import { publicationTypeTitlesHash } from '@culture/utils/helpers/publications';
import { movieTypeTitlesHash } from '@culture/utils/helpers/movies';
import Tooltip from 'components/Tooltip';
import { ImageFormatContext } from 'contexts/ImageFormatContext';
import styles from './styles.module.scss';

const entityTypeTitleHash = {
	institutes: instituteTypeTitlesHash,
	objects: objectTypeTitlesHash,
	movies: movieTypeTitlesHash,
	materials: publicationTypeTitlesHash,
	persons: 'Персона',
	themes: 'Подборка',
	poems: 'Стих',
	books: 'Книга',
	touristRoutes: 'Туристический маршрут',
	events: 'Событие'
};

const entityTypeHash = {
	institutes: 'institutes',
	movies: 'movies',
	materials: 'publications',
	persons: 'persons',
	themes: 'publications',
	poems: 'poems',
	books: 'books',
	touristRoutes: 'touristRoutes',
	objects: 'objects',
	events: 'events'
};

const getEntityTypeTitle = (entityType, entity) => {
	const entityTypeTitle = entityTypeTitleHash[entityType];
	if (typeof entityTypeTitle === 'string') {
		return entityTypeTitle;
	}
	if (typeof entityTypeTitle === 'object' && entity?.type) {
		return entityTypeTitle[entity.type];
	}
	return '';
};

const getEntityTypeAndIdFromLink = (link) => {
	const splittedLink = link.split('/').filter(Boolean);
	const _id = [+splittedLink[1], +splittedLink[2]].filter(Number.isInteger)[0];
	if (!_id) {
		return { type: null, _id: null };
	}
	const typeIndex = splittedLink.findIndex((item) => +item === _id) - 1;
	const type = splittedLink[typeIndex];
	return { type, _id };
};

const EntityLinkTooltip = ({ id, link }) => {
	const [loading, setLoading] = useState(true);
	const [entity, setEntity] = useState(null);
	const [typeTitle, setTypeTitle] = useState('');

	const { _id, type } = getEntityTypeAndIdFromLink(link);

	const entityType = entityTypeHash[type];
	const imageFormat = useContext(ImageFormatContext);

	const imageWidth = ['books', 'persons', 'movies'].includes(entityType)
		? 30
		: 40;

	const imageHeight = 40;

	const imageUrl =
		entity?.thumbnailFile &&
		getImageUrl(entity?.thumbnailFile, {
			format: imageFormat,
			params: {
				crop: 'fill',
				gravity: 'center',
				width: imageWidth * 2,
				height: imageHeight * 2
			}
		});

	useEffect(() => {
		if (entityType) {
			setLoading(true);
			api(`${entityType}/${_id}`, {
				fields: ['_id', 'thumbnailFile', 'title', 'type'].join()
			})
				.then(setEntity)
				.finally(() => setLoading(false));
		}
	}, []);

	useEffect(() => {
		if (entity) {
			setTypeTitle(getEntityTypeTitle(type, entity));
		}
	}, [entity]);

	if (!entityType) {
		return null;
	}

	return (
		<Tooltip id={id} float noArrow>
			<div className={styles.EntityLinkTooltip}>
				{loading ? (
					'Загрузка...'
				) : (
					<>
						{imageUrl ? (
							<img
								alt=""
								style={{ width: imageWidth, height: imageHeight }}
								src={imageUrl}
								className={styles.EntityLinkTooltip_Image}
								loading="lazy"
							/>
						) : null}

						<div className={styles.EntityLinkTooltip_Content}>
							<span className={styles.EntityLinkTooltip_EntityType}>
								{typeTitle}
							</span>
							<span className={styles.EntityLinkTooltip_EntityTitle}>
								{entity?.title}
							</span>
						</div>
					</>
				)}
			</div>
		</Tooltip>
	);
};

EntityLinkTooltip.propTypes = {
	id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	link: PropTypes.string.isRequired
};

export default EntityLinkTooltip;
